import {
    CONFIRMED_REQUEST_DEADLINE_STEPS,
    type DeadlineStep,
    isStatusActionAllowed,
    OPEN_REQUEST_DEADLINE_STEPS,
    type RequestBookingOutcome,
    type RequestOutcome,
    type RequestStatus,
    type RequestStatusBookingContext,
    requiresBookingResolution,
} from "@albertine/types";
import type { ChangeRequestStatusRequest } from "@albertine/types/openapi/lmt";
import { Timestamp } from "firebase/firestore";
import { DeadlineStepToTextMap } from "../../common/types/Deadline";
import type { FirebaseTimestamp } from "../../common/types/FirebaseTimestamp";
import type { Request } from "../../common/types/Request";
import { getDeadlineTimeOptions } from "../../utils/time.util";
import type { MdIconName } from "../Common/Icon";

export type Deadline = {
    nextDeadlineStep: DeadlineStep | null;
    nextDeadlineAt: FirebaseTimestamp | null;
    deadlineCreatedAt: FirebaseTimestamp | null | undefined;
};

type ActionOptions = { question?: string; options: ActionOption[] } | undefined;

const updateNextStepQuestion = "What will you do next?";

const nextStepQuestion = (deadlineStep: DeadlineStep | undefined) => {
    switch (deadlineStep) {
        case "send_status_update":
            return "When you'll send an update?";
        case "send_proposal":
            return "When you'll send a proposal?";
        case "send_booking_confirmation":
            return "When you'll send a confirmation?";
        case "revisit_request":
            return "When will you revisit request?";
        case "send_status_update_internal":
            return "When you'll send an update?";
        case "wait_for_member_response":
            return "When check on member?";
        case "wait_for_supplier_response":
            return "When check on supplier response?";
        case "check_on_pending_payment":
            return "When you'll check on payment?";
        default:
            return "When will you do this?";
    }
};

const updateNextStep: ActionOption = {
    action: "update_next_step",
    icon: "KeyBoardTab",
    label: "Update next step",
};
const markAsConfirmed: ActionOption = {
    action: "mark_as_confirmed",
    icon: "Check",
    label: "Mark as confirmed",
};
const reopenRequest: ActionOption = {
    action: "reopen_request",
    icon: "RotateRight",
    label: "Reopen request",
};
const closeRequest: ActionOption = {
    action: "close_request",
    icon: "Unpublished",
    label: "Close request",
};
const memberCancelled: ActionOption = {
    action: "close_request",
    icon: "Unpublished",
    label: "Cancelled by the member",
    outcome: "memberCancelled",
};
const confirmBookingResolutionFirstBooked: ActionOption = {
    action: "mark_as_confirmed_ask_booking_resolution",
    label: "First choice booked",
    bookingOutcome: "firstChoiceBooked",
};
const confirmBookingResolutionAlternativeAccepted: ActionOption = {
    action: "mark_as_confirmed_ask_booking_resolution",
    label: "Alternative accepted",
    bookingOutcome: "alternativeChoiceBooked",
};
const changeBookingResolution: ActionOption = {
    action: "change_booking_resolution",
    icon: "Check",
    label: "Change booking resolution",
};
const closedRequestInformationOnly: ActionOption = {
    action: "close_request_ask_no_booking",
    label: "Information request only",
    outcome: "noBookingNeeded",
};
const closedRequestNotConverted: ActionOption = {
    action: "close_request_ask_no_booking",
    label: "Not converted to booking",
    outcome: "notBooked",
};
const changeRequestClosedNoBooking: ActionOption = {
    action: "change_request_ask_no_booking",
    icon: "Unpublished",
    label: "Change request resolution",
};

const DeadlineStepMap: Record<DeadlineStep, ActionOption> = {
    send_status_update: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.send_status_update,
        deadlineStep: "send_status_update",
        isInternal: false,
    },
    send_proposal: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.send_proposal,
        deadlineStep: "send_proposal",
        isInternal: false,
    },
    send_booking_confirmation: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.send_booking_confirmation,
        deadlineStep: "send_booking_confirmation",
        isInternal: false,
    },
    no_further_action: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.no_further_action,
        deadlineStep: "no_further_action",
        isInternal: true,
        outcome: "noActionNeeded",
    },
    revisit_request: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.revisit_request,
        deadlineStep: "revisit_request",
        isInternal: true,
    },
    send_status_update_internal: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.send_status_update_internal,
        deadlineStep: "send_status_update_internal",
        isInternal: true,
    },
    wait_for_member_response: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.wait_for_member_response,
        deadlineStep: "wait_for_member_response",
        isInternal: true,
    },
    wait_for_supplier_response: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.wait_for_supplier_response,
        deadlineStep: "wait_for_supplier_response",
        isInternal: false,
    },
    check_on_pending_payment: {
        action: "update_next_step_deadline_selection",
        label: DeadlineStepToTextMap.check_on_pending_payment,
        deadlineStep: "check_on_pending_payment",
        isInternal: true,
    },
};

export type Action =
    | "close_request"
    | "close_request_ask_no_booking"
    | "change_request_ask_no_booking"
    | "request_ask_no_booking_changed"
    | "mark_as_confirmed"
    | "mark_as_confirmed_ask_booking_resolution"
    | "change_booking_resolution"
    | "booking_resolution_changed"
    | "reopen_request"
    | "update_next_step"
    | "update_next_step_deadline_selection"
    | "update_next_step_deadline_time_selection";

export type ActionOption = {
    action: Action;
    icon?: MdIconName;
    label: string;
    previous?: ActionOption | undefined;
    outcome?: RequestOutcome | null;
    bookingOutcome?: RequestBookingOutcome | null;
    deadlineStep?: DeadlineStep | undefined;
    nextDeadlineAt?: Date | null | undefined;
    isInternal?: boolean;
};

type RequestStatusActionChange = "action_changed" | "status_changed";

const getNextStepsForAction = (
    currentAction: ActionOption | undefined,
    currentStatus: RequestStatus,
    askForBookingResolution: boolean,
): ActionOptions => {
    switch (currentAction?.action) {
        case "update_next_step":
            if (
                currentStatus === "new" ||
                currentStatus === "draft" ||
                currentStatus === "open"
            ) {
                return {
                    question: updateNextStepQuestion,
                    options: OPEN_REQUEST_DEADLINE_STEPS.map(
                        (step) => DeadlineStepMap[step],
                    ),
                };
            }

            if (currentStatus === "confirmed") {
                return {
                    question: updateNextStepQuestion,
                    options: CONFIRMED_REQUEST_DEADLINE_STEPS.map(
                        (step) => DeadlineStepMap[step],
                    ),
                };
            }
            break;

        case "mark_as_confirmed":
            if (askForBookingResolution) {
                return {
                    question: "What was the resolution?",
                    options: [
                        confirmBookingResolutionFirstBooked,
                        confirmBookingResolutionAlternativeAccepted,
                    ],
                };
            }
            return {
                question: updateNextStepQuestion,
                options: CONFIRMED_REQUEST_DEADLINE_STEPS.map(
                    (step) => DeadlineStepMap[step],
                ),
            };

        case "change_booking_resolution":
            return {
                question: "What was the resolution?",
                options: [
                    {
                        ...confirmBookingResolutionFirstBooked,
                        action: "booking_resolution_changed",
                    },
                    {
                        ...confirmBookingResolutionAlternativeAccepted,
                        action: "booking_resolution_changed",
                    },
                ],
            };

        case "booking_resolution_changed":
            return undefined;

        case "mark_as_confirmed_ask_booking_resolution":
            return {
                question: updateNextStepQuestion,
                options: CONFIRMED_REQUEST_DEADLINE_STEPS.map(
                    (step) => DeadlineStepMap[step],
                ),
            };

        case "close_request":
            if (currentAction.outcome === "memberCancelled") return undefined;
            return {
                question: "Closed because of?",
                options: [closedRequestInformationOnly, closedRequestNotConverted],
            };

        case "change_request_ask_no_booking":
            return {
                question: "Closed because of?",
                options: [
                    {
                        ...closedRequestInformationOnly,
                        action: "request_ask_no_booking_changed",
                    },
                    {
                        ...closedRequestNotConverted,
                        action: "request_ask_no_booking_changed",
                    },
                ],
            };

        case "request_ask_no_booking_changed":
            return undefined;

        case "reopen_request":
            return {
                question: updateNextStepQuestion,
                options: OPEN_REQUEST_DEADLINE_STEPS.map(
                    (step) => DeadlineStepMap[step],
                ),
            };

        case "update_next_step_deadline_selection":
            return {
                question: nextStepQuestion(currentAction.deadlineStep),
                options: getDeadlineTimeOptions().map((option) => ({
                    action: "update_next_step_deadline_time_selection",
                    label: option.label,
                    deadlineStep: currentAction.deadlineStep,
                    nextDeadlineAt: option.value,
                })),
            };

        default:
            break;
    }
    return undefined;
};

export const getNextSteps = (
    currentAction: ActionOption | undefined,
    currentStatus: RequestStatus,
    requestHasConfirmedBookings: boolean,
    requestHasCancelledBookings: boolean,
    askForBookingResolution: boolean,
): ActionOptions => {
    const bookings: RequestStatusBookingContext = {
        hasConfirmedBookings: requestHasConfirmedBookings,
        hasCancelledBookings: requestHasCancelledBookings,
    };
    const actionNextSteps = getNextStepsForAction(
        currentAction,
        currentStatus,
        askForBookingResolution,
    );
    if (actionNextSteps) return actionNextSteps;

    switch (currentStatus) {
        case "draft":
        case "new":
            return {
                question: updateNextStepQuestion,
                options: OPEN_REQUEST_DEADLINE_STEPS.map(
                    (step) => DeadlineStepMap[step],
                ),
            };
        case "open": {
            const options: ActionOption[] = [];
            if (
                isStatusActionAllowed({
                    action: "set_next_step",
                    intent: "update",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(updateNextStep);
            }
            if (
                isStatusActionAllowed({
                    action: "set_next_step",
                    intent: "confirm",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(markAsConfirmed);
            }
            if (
                isStatusActionAllowed({
                    action: "close",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(closeRequest);
            }
            return { options };
        }
        case "confirmed": {
            const options: ActionOption[] = [];
            if (
                isStatusActionAllowed({
                    action: "set_next_step",
                    intent: "update",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(updateNextStep);
            }
            if (
                isStatusActionAllowed({
                    action: "set_next_step",
                    intent: "reopen",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(reopenRequest);
            }
            if (
                isStatusActionAllowed({
                    action: "change_booking_resolution",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(changeBookingResolution);
            } else if (
                isStatusActionAllowed({
                    action: "close",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(
                    requestHasCancelledBookings ? memberCancelled : closeRequest,
                );
            }
            return { options };
        }
        case "closed": {
            const options: ActionOption[] = [];
            if (
                isStatusActionAllowed({
                    action: "set_next_step",
                    intent: "reopen",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(reopenRequest);
            }
            if (
                isStatusActionAllowed({
                    action: "change_closed_resolution",
                    currentStatus,
                    bookings,
                })
            ) {
                options.push(changeRequestClosedNoBooking);
            }
            return { options };
        }
        default:
            return undefined;
    }
};

const actionToNextStep = (
    selectedAction: ActionOption,
    currentAction: ActionOption | undefined,
): ActionOption => {
    const newOutcome =
        selectedAction.action === "reopen_request"
            ? null
            : (selectedAction.outcome ?? currentAction?.outcome);
    const newBookingOutcome =
        selectedAction.action === "reopen_request"
            ? null
            : (selectedAction.bookingOutcome ?? currentAction?.bookingOutcome);
    return {
        ...selectedAction,
        previous: currentAction,
        deadlineStep: selectedAction.deadlineStep ?? currentAction?.deadlineStep,
        nextDeadlineAt: selectedAction.nextDeadlineAt ?? currentAction?.nextDeadlineAt,
        outcome: newOutcome,
        bookingOutcome: newBookingOutcome,
    };
};

const hasPreviousAction = (actionId: Action, action: ActionOption | undefined) => {
    let current: ActionOption | undefined = action;
    while (current !== undefined) {
        if (current.action === actionId) return true;
        current = current.previous;
    }
    return false;
};

const newStatusForAction = (
    action: ActionOption,
    currentStatus: RequestStatus,
): RequestStatus => {
    if (action.deadlineStep === "no_further_action") return "confirmed";
    if (hasPreviousAction("reopen_request", action)) return "open";
    switch (action.action) {
        case "close_request":
        case "close_request_ask_no_booking":
            return "closed";
        case "update_next_step_deadline_selection":
        case "update_next_step_deadline_time_selection":
        case "update_next_step":
            return currentStatus === "confirmed" ||
                hasPreviousAction("mark_as_confirmed", action)
                ? "confirmed"
                : "open";
        case "reopen_request":
            return "open";
        case "mark_as_confirmed":
        case "mark_as_confirmed_ask_booking_resolution":
            return "confirmed";
        default:
            return currentStatus;
    }
};

export const shouldAskForBookingResolution = (request: Request): boolean =>
    requiresBookingResolution(request.offeringCategory);

export function buildChangeRequestStatusRequest(
    nextAction: ActionOption,
): ChangeRequestStatusRequest {
    if (nextAction.action === "booking_resolution_changed") {
        if (nextAction.bookingOutcome == null) {
            throw new Error("booking_resolution_changed requires bookingOutcome");
        }
        return {
            action: "change_booking_resolution",
            bookingOutcome: nextAction.bookingOutcome,
        };
    }

    if (nextAction.action === "request_ask_no_booking_changed") {
        if (nextAction.outcome == null) {
            throw new Error("request_ask_no_booking_changed requires outcome");
        }
        return {
            action: "change_closed_resolution",
            outcome: nextAction.outcome,
        };
    }

    if (
        nextAction.action === "close_request" ||
        nextAction.action === "close_request_ask_no_booking"
    ) {
        return {
            action: "close",
            ...(nextAction.outcome !== undefined && { outcome: nextAction.outcome }),
        };
    }

    if (nextAction.deadlineStep === "no_further_action") {
        return {
            action: "confirm_no_further_action",
            ...(nextAction.bookingOutcome !== undefined && {
                bookingOutcome: nextAction.bookingOutcome,
            }),
        };
    }

    if (nextAction.deadlineStep == null || nextAction.nextDeadlineAt == null) {
        throw new Error("set_next_step requires deadline step and time");
    }

    const intent = hasPreviousAction("reopen_request", nextAction)
        ? "reopen"
        : hasPreviousAction("mark_as_confirmed", nextAction) ||
            hasPreviousAction("mark_as_confirmed_ask_booking_resolution", nextAction)
          ? "confirm"
          : "update";

    return {
        action: "set_next_step",
        intent,
        nextDeadlineStep: nextAction.deadlineStep,
        nextDeadlineAt: nextAction.nextDeadlineAt.toISOString(),
        ...(intent === "reopen" && {
            outcome: null,
            bookingOutcome: null,
        }),
        ...(intent !== "reopen" &&
            nextAction.outcome !== undefined && { outcome: nextAction.outcome }),
        ...(intent !== "reopen" &&
            nextAction.bookingOutcome !== undefined && {
                bookingOutcome: nextAction.bookingOutcome,
            }),
    };
}

export const getRequestStatusChange = (
    request: Request,
    selectedAction: ActionOption,
    currentAction: ActionOption | undefined,
): {
    change: RequestStatusActionChange;
    nextAction: ActionOption;
    newStatus: RequestStatus | undefined;
    newDeadline: Deadline | undefined;
    newOutcome: RequestOutcome | null | undefined;
    newBookingOutcome: RequestBookingOutcome | null | undefined;
} => {
    const currentStatus = request.status;
    const nextAction = actionToNextStep(selectedAction, currentAction);
    const nextActionSteps = getNextStepsForAction(
        nextAction,
        currentStatus,
        shouldAskForBookingResolution(request),
    );
    const noFollowUpActions =
        !nextActionSteps || nextAction.deadlineStep === "no_further_action";
    if (noFollowUpActions) {
        const newDeadline =
            nextAction.deadlineStep && nextAction?.nextDeadlineAt
                ? {
                      nextDeadlineAt: Timestamp.fromDate(nextAction.nextDeadlineAt),
                      nextDeadlineStep: nextAction.deadlineStep,
                      deadlineCreatedAt: Timestamp.now(),
                  }
                : {
                      nextDeadlineAt: null,
                      nextDeadlineStep: null,
                      deadlineCreatedAt: null,
                  };

        const newStatus = newStatusForAction(nextAction, currentStatus);
        const doNotChangeDeadline = nextAction.action === "booking_resolution_changed";

        return {
            change: "status_changed",
            newStatus,
            newDeadline: doNotChangeDeadline ? undefined : newDeadline,
            newOutcome: nextAction.outcome,
            newBookingOutcome: nextAction.bookingOutcome,
            nextAction,
        };
    }
    return {
        change: "action_changed",
        nextAction,
        newStatus: undefined,
        newDeadline: undefined,
        newOutcome: undefined,
        newBookingOutcome: undefined,
    };
};
